import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMutation } from '@tanstack/react-query';
import { Box, BulletList, BulletListItem, Column, ConfirmationModal, Row, Spinner, Typography } from 'seagull';
import { Checkmark, ExclamationLine14, InformationLiner24 } from 'seagull/icons';
import { useStoreActions } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import styles from './ContactsBulkActionResultModal.module.scss';
const bulkActionBiEventIds = {
    archive: {
        modalViewed: 11613,
        results: 11615
    },
    delete: {
        modalViewed: 11614,
        results: 11616
    }
};
function renderResultContent(result, actionType, t) {
    return /*#__PURE__*/ _jsxs(Column, {
        children: [
            result.successful > 0 && /*#__PURE__*/ _jsxs(Row, {
                alignItems: "center",
                gap: 8,
                className: styles.successSection,
                children: [
                    /*#__PURE__*/ _jsx(Checkmark, {}),
                    ' ',
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph1",
                        testId: "bulk-succeeded-text",
                        children: t.successMessage({
                            count: result.successful,
                            actionType
                        })
                    })
                ]
            }),
            result.skipped > 0 && /*#__PURE__*/ _jsxs(Row, {
                alignItems: "center",
                gap: 8,
                className: styles.skippedSection,
                children: [
                    /*#__PURE__*/ _jsx(InformationLiner24, {}),
                    ' ',
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "paragraph2",
                        weight: "normal",
                        testId: "bulk-skipped-text",
                        children: t.skippedMessage({
                            count: result.skipped
                        })
                    })
                ]
            }),
            result.failed > 0 && /*#__PURE__*/ _jsxs(Column, {
                className: styles.errorSection,
                children: [
                    /*#__PURE__*/ _jsxs(Row, {
                        alignItems: "start",
                        gap: 8,
                        children: [
                            /*#__PURE__*/ _jsx(ExclamationLine14, {}),
                            ' ',
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: "paragraph2",
                                testId: "bulk-failed-text",
                                children: t.failureMessage({
                                    count: result.failed,
                                    actionType
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(BulletList, {
                        spacing: "compact",
                        className: styles.failedContactsList,
                        children: result.failedResults.map((failed)=>/*#__PURE__*/ _jsx(BulletListItem, {
                                custom: true,
                                bulletSize: "small",
                                className: styles.failedContactItem,
                                testId: "bulk-failed-contact-".concat(failed.id),
                                children: /*#__PURE__*/ _jsxs(Typography, {
                                    variant: "paragraph3",
                                    children: [
                                        /*#__PURE__*/ _jsx(Typography, {
                                            variant: "paragraph3",
                                            as: "span",
                                            weight: "bold",
                                            children: failed.companyName
                                        }),
                                        ' ',
                                        "- ",
                                        failed.contactName,
                                        " (",
                                        failed.email,
                                        ")"
                                    ]
                                })
                            }, failed.id))
                    })
                ]
            })
        ]
    });
}
const ContactsBulkActionResultModal = (param)=>{
    let { actionType, contactIds, dismissModal, onComplete } = param;
    const { api, logBI } = useStoreActions();
    const { t } = useLocaleKeys('pages.contactsDashboard.bulkActionResult');
    const { mutate: sendBulkRequest, data: result, isPending: isBulkInProgress, isError } = useMutation({
        mutationFn: ()=>actionType === 'archive' ? api.contact.bulkArchive({
                ids: contactIds
            }) : api.contact.bulkDelete({
                ids: contactIds
            }),
        onSuccess: (data)=>{
            logBI(bulkActionBiEventIds[actionType].results, {
                successful: data.successful,
                failed: data.failed,
                skipped: data.skipped
            });
            onComplete();
        }
    });
    useOnMount(()=>{
        logBI(bulkActionBiEventIds[actionType].modalViewed, {
            count: contactIds.length
        });
        sendBulkRequest();
    });
    const renderContent = ()=>{
        if (isBulkInProgress) {
            return /*#__PURE__*/ _jsx(Box, {
                className: styles.loadingContainer,
                children: /*#__PURE__*/ _jsx(Spinner, {
                    testId: "bulk-action-spinner"
                })
            });
        }
        if (isError) {
            return /*#__PURE__*/ _jsx(Column, {
                className: styles.errorSection,
                children: /*#__PURE__*/ _jsxs(Row, {
                    alignItems: "start",
                    gap: 4,
                    children: [
                        /*#__PURE__*/ _jsx(ExclamationLine14, {}),
                        ' ',
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "paragraph2",
                            testId: "bulk-request-error-text",
                            children: t.requestErrorMessage()
                        })
                    ]
                })
            });
        }
        if (result) {
            return renderResultContent(result, actionType, t);
        }
        return null;
    };
    return /*#__PURE__*/ _jsx(ConfirmationModal, {
        title: t.title[actionType](),
        textAlignCenter: false,
        width: "680px",
        maxHeight: "700px",
        primaryButtonLabel: t.closeButton(),
        primaryButtonDisabled: isBulkInProgress,
        cancelButtonDisabled: isBulkInProgress,
        onPrimaryButtonClick: ()=>dismissModal(true),
        onCloseButtonClick: ()=>dismissModal(true),
        content: renderContent()
    });
};
export default ContactsBulkActionResultModal;
